import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import { useContext, useEffect, useState, useCallback } from "react";
import Home from "./components/Home";
import AuthContext from "./store/auth-context";
import {
  createTheme,
  ThemeProvider,
  StylesProvider,
  jssPreset,
} from "@material-ui/core/styles";
import { create } from "jss";
import rtl from "jss-rtl";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthLayout from "./components/AuthLayout";
import Loader from "./components/common/Loader";
import Register from "./components/login/Register";
import { loginservice } from "./services/login.service";
import ValetLogin from "./components/valetParking/ValetLogin";
import ValetLayout from "./components/valetParking/ValetLayout";
import RegisterUAEPass from "./components/login/RegisterUAEPass";
import OtpForLogin from "./components/login/OtpForLogin";
import GTMPathTracker from "./components/GTM/GTMPathTracker";
import { 
  restoreBrandFromUAEPass, 
  isReturningFromUAEPass, 
  getCurrentBrand, 
  isBrandLocked 
} from "./utils/brandUtils";

const theme = createTheme({
  direction: "rtl",
});

const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

function App() {
  const authCtx = useContext(AuthContext);
  const [logo, setLogo] = useState<string>("");
  const [brand, setBrandText] = useState<string>("");
  const [brandNameForWPBaseUrl, setBrandNameForWpBaseUrl] = useState<string>("");

  const burjeelBrand = "burjeel";
  const medeorBrand = "medeor";
  const lifecareBrand = "lifecare";
  const colabBrand = "colab";
  const llhBrand = "llh";
  const bmcBrand = "bmc";

  // Centralized single source of truth for structural layout mappings
  const applyBrandAssets = useCallback((targetBrand: string) => {
    if (!targetBrand || targetBrand === "null") return;

    const normalizedBrand = targetBrand.toLowerCase().trim();
    const themeElement = document.getElementById("theme") as HTMLLinkElement | null;
    const faviconElement = document.getElementById("favicon") as HTMLLinkElement | null;

    switch (normalizedBrand) {
      case burjeelBrand:
        setLogo(authCtx.selectedLanguage === "en" ? "/assets/images/burjeel-logo-EN.png" : "/assets/images/burjeel-logo-AR.png");
        setBrandText("Burjeel");
        if (themeElement) themeElement.href = "/css/theme-burjeel.css";
        document.title = "Burjeel | Patient Portal";
        if (faviconElement) faviconElement.href = "/assets/images/burjeel-favicon.png";
        setBrandNameForWpBaseUrl(burjeelBrand);
        break;

      case medeorBrand:
        setLogo("/assets/images/medeor-logo.png");
        setBrandText("Medeor");
        if (themeElement) themeElement.href = "/css/theme-medeor.css";
        document.title = "Medeor | Patient Portal";
        if (faviconElement) faviconElement.href = "/assets/images/medeor-favicon.png";
        setBrandNameForWpBaseUrl(medeorBrand);
        break;

      case lifecareBrand:
        setLogo("/assets/images/lifecare_icon.png");
        setBrandText("Lifecare");
        if (themeElement) themeElement.href = "/css/theme-lifecare.css";
        document.title = "Lifecare | Patient Portal";
        if (faviconElement) faviconElement.href = "/assets/images/lifecare-favicon.png";
        setBrandNameForWpBaseUrl(lifecareBrand);
        break;

      case colabBrand:
        setLogo("/assets/images/colab-logo.png");
        setBrandText("coLAB");
        if (themeElement) themeElement.href = "/css/theme-colab.css";
        document.title = "coLAB | Patient Portal";
        if (faviconElement) faviconElement.href = "/assets/images/colab-favicon.png";
        setBrandNameForWpBaseUrl(colabBrand);
        break;

      case llhBrand:
        setLogo("/assets/images/llh-logo.png");
        setBrandText("LLH");
        if (themeElement) themeElement.href = "/css/theme-llh.css";
        document.title = "LLH | Patient Portal";
        if (faviconElement) faviconElement.href = "/assets/images/llh-favicon.png";
        setBrandNameForWpBaseUrl(llhBrand);
        break;

      case bmcBrand:
        setLogo("/assets/images/BMC-eng.png");
        setBrandText("BMC");
        if (themeElement) themeElement.href = "/css/theme-bmc.css";
        document.title = "BMC | Patient Portal";
        if (faviconElement) faviconElement.href = "/assets/images/bmc-favicon.png";
        setBrandNameForWpBaseUrl(bmcBrand);
        break;

      default:
        // Absolute fallback core defaults
        setLogo(authCtx.selectedLanguage === "en" ? "/assets/images/burjeel-logo-EN.png" : "/assets/images/burjeel-logo-AR.png");
        setBrandText("Burjeel");
        if (themeElement) themeElement.href = "/css/theme-burjeel.css";
        document.title = "Burjeel | Patient Portal";
        if (faviconElement) faviconElement.href = "/assets/images/burjeel-favicon.png";
        setBrandNameForWpBaseUrl(burjeelBrand);
        break;
    }
  }, [authCtx.selectedLanguage]);

  // Initial Entry Evaluation Effect Hook
  useEffect(() => {
    console.log('APP COMPONENT - Initial entry evaluation processing');
    
    const params = new URLSearchParams(window.location.search);
    const urlBrandName = params.get("brandName");
    const isUAEPassReturn = isReturningFromUAEPass();

    let runtimeBrandTarget: string | null = null;

    // 1. If returning from external UAE Pass flow, restore from deep cache memory
    if (isUAEPassReturn) {
      console.log('[App] UAEPass landing sequence — restoring state brand');
      runtimeBrandTarget = restoreBrandFromUAEPass();
    } 
    // 2. If explicit query parameter string context exists in navigation target route
    else if (urlBrandName) {
      runtimeBrandTarget = urlBrandName.toLowerCase().trim();
      localStorage.setItem("brandName", runtimeBrandTarget);
    } 
    // 3. Fallback to reading baseline local configuration context state
    else {
      const storedContext = localStorage.getItem("brandName");
      if (storedContext && storedContext !== "null") {
        runtimeBrandTarget = storedContext;
      }
    }

    // 4. Default baseline safety fallback check if all criteria are empty
    if (!runtimeBrandTarget && !isBrandLocked()) {
      runtimeBrandTarget = burjeelBrand;
      localStorage.setItem("brandName", burjeelBrand);
    }

    console.log('[App] Calculated active layout string context target:', runtimeBrandTarget);
    
    if (runtimeBrandTarget) {
      applyBrandAssets(runtimeBrandTarget);
    }
  }, [applyBrandAssets]);

  // Global Cross-tab and Storage Synchronization Listener Lifecycle hook
  useEffect(() => {
    const handleBrandChange = () => {
      const storedBrandName = getCurrentBrand();
      console.log('Storage execution event catch - Brand mapped to:', storedBrandName);
      if (storedBrandName) {
        applyBrandAssets(storedBrandName);
      }
    };

    window.addEventListener('storage', handleBrandChange);
    return () => {
      window.removeEventListener('storage', handleBrandChange);
    };
  }, [applyBrandAssets]);

  // Wordpress Asset Integration Sync Hook
  useEffect(() => {
    if (brandNameForWPBaseUrl !== "") {
      getWordpressBaseUrl();
    }
    async function getWordpressBaseUrl() {
      let res = await loginservice.getWpBaseUrl(brandNameForWPBaseUrl);
      if (res && res.data) {
        localStorage.setItem("wpBaseUrl", res.data);
        authCtx.setWpBaseUrl(res.data);
      }
    }
  }, [brandNameForWPBaseUrl, authCtx]);

  return (
    <>
      <ErrorBoundary>
        <Router>
          <GTMPathTracker />
        </Router>

        {authCtx.selectedLanguage === "ar" ? (
          <StylesProvider jss={jss}>
            <ThemeProvider theme={theme}>
              <div dir="rtl">
                <Router>
                  <Switch>
                    <Route exact path="/"><Redirect to="/home/login" /></Route>
                    <Route path="/home"><Home logo={logo} brand={brand} /></Route>
                    <Route path="/home/register"><Register /></Route>
                    <Route path="/home/UAEregister"><RegisterUAEPass /></Route>
                    <Route path="/home/otpforlogin"><OtpForLogin /></Route>
                    <ProtectedRoute path="/auth" component={AuthLayout}></ProtectedRoute>
                  </Switch>
                </Router>
                {authCtx.isLoader && <Loader />}
              </div>
            </ThemeProvider>
          </StylesProvider>
        ) : (
          <div>
            <Router>
              <Switch>
                <Route exact path="/"><Redirect to="/home/login" /></Route>
                <Route path="/home"><Home logo={logo} brand={brand} /></Route>
                <Route path="/home/register"><Register /></Route>
                <Route path="/home/UAEregister"><RegisterUAEPass /></Route>
                <Route path="/home/otpforlogin"><OtpForLogin /></Route>
                <ProtectedRoute path="/auth" component={AuthLayout}></ProtectedRoute>
              </Switch>
            </Router>
            {authCtx.isLoader && <Loader />}
          </div>
        )}
      </ErrorBoundary>
      
      <ErrorBoundary>
        <Router>
          <Switch>
            <Route exact path="/valet"><Redirect to="/valet/login" /></Route>
            <Route path="/valet/login"><ValetLogin /></Route>
            <Route path="/valetauth"><ValetLayout /></Route>
          </Switch>
        </Router>
      </ErrorBoundary>
    </>
  );
}

export default App;